import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { EnvironmentalEffectTexts, LifeCycleStageTexts, TextsLcaData } from 'src/app/model/tooltips-lca-data';

export const ECOINVENT_VERSION = "3.10";

export interface LcaEffectInfo {
  key: string;
  name: string;
  description: string;
  unit: string;
  unitDescription: string;
}

export interface LcaStageInfo {
  key: string;
  name: string;
  description: string;
}

@Injectable({ providedIn: 'root' })
export class LifeCycleAnalysisInformation {

  environmentalEffectsInformation: LcaEffectInfo[] = [];
  lifeCycleStageInformation: LcaStageInfo[] = [];

  constructor(translateService: TranslocoService) {
    const textsData = new TextsLcaData(translateService);
    this.addEnvironmentalEffects(textsData.environmentalEffectsTexts, translateService);
    this.addLifeCycleStages(textsData.lifeCycleStagesTexts, translateService);
  }

  private addEnvironmentalEffects(texts: EnvironmentalEffectTexts, translateService: TranslocoService) {

    this.environmentalEffectsInformation.push({
      key: 'climateChange',
      name: translateService.translate('analysis.lifecycleAnalysis.climateChange'),
      unit: translateService.translate('analysis.lifecycleAnalysis.climateChangeUnit'),
      description: texts.climateChange.description,
      unitDescription: texts.climateChange.unit
    });
    this.environmentalEffectsInformation.push({
      key: 'freshwaterAndTerrestrialAcidification',
      name: translateService.translate('analysis.lifecycleAnalysis.acidification'),
      unit: translateService.translate('analysis.lifecycleAnalysis.acidificationUnit'),
      description: texts.acidification.description,
      unitDescription: texts.acidification.unit
    });
    this.environmentalEffectsInformation.push({
      key: 'freshwaterEcotoxicity',
      name: translateService.translate('analysis.lifecycleAnalysis.freshwaterEcotoxicity'),
      unit: translateService.translate('analysis.lifecycleAnalysis.freshwaterEcotoxicityUnit'),
      description: texts.ecotoxicityFreshwater.description,
      unitDescription: texts.ecotoxicityFreshwater.unit
    });
    this.environmentalEffectsInformation.push({
      key: 'freshwaterEutrophication',
      name: translateService.translate('analysis.lifecycleAnalysis.freshwaterEutrophication'),
      unit: translateService.translate('analysis.lifecycleAnalysis.freshwaterEutrophicationUnit'),
      description: texts.eutrophicationFreshwater.description,
      unitDescription: texts.eutrophicationFreshwater.unit
    });
    this.environmentalEffectsInformation.push({
      key: 'marineEutrophication',
      name: translateService.translate('analysis.lifecycleAnalysis.marineEutrophication'),
      unit: translateService.translate('analysis.lifecycleAnalysis.marineEutrophicationUnit'),
      description: texts.eutrophicationMarine.description,
      unitDescription: texts.eutrophicationMarine.unit
    });
    this.environmentalEffectsInformation.push({
      key: 'terrestrialEutrophication',
      name: translateService.translate('analysis.lifecycleAnalysis.terrestrialEutrophication'),
      unit: translateService.translate('analysis.lifecycleAnalysis.terrestrialEutrophicationUnit'),
      description: texts.eutrophicationTerrestrial.description,
      unitDescription: texts.eutrophicationTerrestrial.unit
    });
    this.environmentalEffectsInformation.push({
      key: 'carcinogenicEffects',
      name: translateService.translate('analysis.lifecycleAnalysis.carcinogenicEffects'),
      unit: translateService.translate('analysis.lifecycleAnalysis.carcinogenicEffectsUnit'),
      description: texts.cancerogenicEffects.description,
      unitDescription: texts.cancerogenicEffects.unit
    });
    this.environmentalEffectsInformation.push({
      key: 'ionizingRadiation',
      name: translateService.translate('analysis.lifecycleAnalysis.ionizingRadiation'),
      unit: translateService.translate('analysis.lifecycleAnalysis.ionizingRadiationUnit'),
      description: texts.ionizingRadiation.description,
      unitDescription: texts.ionizingRadiation.unit
    });
    this.environmentalEffectsInformation.push({
      key: 'nonCarcinogenicEffects',
      name: translateService.translate('analysis.lifecycleAnalysis.nonCarcinogenicEffects'),
      unit: translateService.translate('analysis.lifecycleAnalysis.nonCarcinogenicEffectsUnit'),
      description: texts.noncancerogenicEffects.description,
      unitDescription: texts.noncancerogenicEffects.unit
    });
    this.environmentalEffectsInformation.push({
      key: 'ozoneLayerDepletion',
      name: translateService.translate('analysis.lifecycleAnalysis.ozoneLayerDepletion'),
      unit: translateService.translate('analysis.lifecycleAnalysis.ozoneLayerDepletionUnit'),
      description: texts.ozoneLayerDepletion.description,
      unitDescription: texts.ozoneLayerDepletion.unit
    });
    this.environmentalEffectsInformation.push({
      key: 'photochemicalOzoneCreationPotential',
      name: translateService.translate('analysis.lifecycleAnalysis.photochemicalOzoneCreationPotential'),
      unit: translateService.translate('analysis.lifecycleAnalysis.photochemicalOzoneCreationPotentialUnit'),
      description: texts.photochemicalOzoneCreation.description,
      unitDescription: texts.photochemicalOzoneCreation.unit
    });
    this.environmentalEffectsInformation.push({
      key: 'respiratoryEffects',
      name: translateService.translate('analysis.lifecycleAnalysis.respiratoryEffects'),
      unit: translateService.translate('analysis.lifecycleAnalysis.respiratoryEffectsUnit'),
      description: texts.respiratoryEffects.description,
      unitDescription: texts.respiratoryEffects.unit
    });
    this.environmentalEffectsInformation.push({
      key: 'waterScarcity',
      name: translateService.translate('analysis.lifecycleAnalysis.waterScarcity'),
      unit: translateService.translate('analysis.lifecycleAnalysis.waterScarcityUnit'),
      description: texts.waterScarcity.description,
      unitDescription: texts.waterScarcity.unit
    });
    this.environmentalEffectsInformation.push({
      key: 'fossils',
      name: translateService.translate('analysis.lifecycleAnalysis.fossils'),
      unit: translateService.translate('analysis.lifecycleAnalysis.fossilsUnit'),
      description: texts.fossils.description,
      unitDescription: texts.fossils.unit
    });
    this.environmentalEffectsInformation.push({
      key: 'landUse',
      name: translateService.translate('analysis.lifecycleAnalysis.landUse'),
      unit: translateService.translate('analysis.lifecycleAnalysis.landUseUnit'),
      description: texts.landUse.description,
      unitDescription: texts.landUse.unit
    });
    this.environmentalEffectsInformation.push({
      key: 'mineralsAndMetals',
      name: translateService.translate('analysis.lifecycleAnalysis.mineralsAndMetals'),
      unit: translateService.translate('analysis.lifecycleAnalysis.mineralsAndMetalsUnit'),
      description: texts.mineralsAndMetals.description,
      unitDescription: texts.mineralsAndMetals.unit
    });
  }

  private addLifeCycleStages(texts: LifeCycleStageTexts, translateService: TranslocoService) {

    this.lifeCycleStageInformation.push({
      key: 'environmentalEffectsMaterials',
      name: translateService.translate('analysis.lifecycleAnalysis.materials'),
      description: '',
    });
    /* this.lifeCycleStageInformation.push({
      key: 'environmentalEffectsPrimaryMaterials',
      name: translateService.translate('analysis.lifecycleAnalysis.virginMaterials'),
      description: texts.primaryMaterials,
    });
    this.lifeCycleStageInformation.push({
      key: 'environmentalEffectsSecondaryMaterials',
      name: translateService.translate('analysis.lifecycleAnalysis.recycledMaterials'),
      description: texts.secondaryMaterials,
    });
    this.lifeCycleStageInformation.push({
      key: 'environmentalEffectsManufacturing',
      name: translateService.translate('analysis.lifecycleAnalysis.manufacturing'),
      description: '',
    }); */
    this.lifeCycleStageInformation.push({
      key: 'environmentalEffectsTransport',
      name: translateService.translate('analysis.lifecycleAnalysis.transport'),
      description: '',
    });
    this.lifeCycleStageInformation.push({
      key: 'environmentalEffectsDistribution',
      name: translateService.translate('analysis.lifecycleAnalysis.distribution'),
      description: '',
    });
    this.lifeCycleStageInformation.push({
      key: 'environmentalEffectsEndOfLife',
      name: translateService.translate('analysis.lifecycleAnalysis.endOfLife'),
      description: '',
    });
    this.lifeCycleStageInformation.push({
      key: 'environmentalEffectsTotal',
      name: translateService.translate('analysis.lifecycleAnalysis.total'),
      description: '',
    });
  }
}
